/* eslint-disable react-refresh/only-export-components */
import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from 'react';
import type { ReactNode } from 'react';
import { resolveInitialLang, loadTranslations } from '@/utils/i18n';
import { LANG_STORAGE_KEY } from '@/utils/constants';

type Translations = Record<string, string>;

interface I18nContextValue {
  lang: string;
  t: (key: string, params?: Record<string, string>) => string;
  setLang: (lang: string) => void;
  isLoading: boolean;
}

export const I18nContext = createContext<I18nContextValue>({
  lang: 'en',
  t: (key) => key,
  setLang: () => {},
  isLoading: false,
});

export function useI18n() {
  return useContext(I18nContext);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [translations, setTranslations] = useState<Translations>({});
  const [lang, setLangState] = useState(() => resolveInitialLang());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadTranslations(lang)
      .then((result) => {
        if (!cancelled) {
          setTranslations(result);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [lang]);

  const setLang = useCallback((newLang: string) => {
    setLangState(newLang);
    localStorage.setItem(LANG_STORAGE_KEY, newLang);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string>) => {
      let text = translations[key] || key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
        });
      }
      return text;
    },
    [translations],
  );

  return (
    <I18nContext.Provider value={{ lang, t, setLang, isLoading }}>
      {children}
    </I18nContext.Provider>
  );
}
